import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {BioentitySummary} from 'folder-items/FileConstants';
import {
  SchemaField,
  RequiredLink,
  RequiredLinkTagSchema,
  RequiredLinkFolderItemType,
} from 'schemas/fields/SchemaFieldConstants';
import pick from 'util/pick';

import * as BulkAntibodyConstants from './BulkAntibodyConstants';
import {BFISpec, BFITag, BioentityLinkSpec, BulkAntibody, ForServerBFITag} from './BulkAntibodyConstants';

export function createBulkAntibody(): BulkAntibody {
  return {
    step: BulkAntibodyConstants.Step.BULK_TRANSLATION,
    // Input parameters
    tagSchemaId: undefined,
    antibodyNamePrefix: '',
    outputFolderId: undefined,
    bioentityLinkSpecs: [],
    // Input files to match to bioentity link fields. Generated by fetching the files in the bulk translation
    // output folder.
    inputFiles: [],
    // Loading state
    inputFilesLoading: false,
    isCreating: false,
  };
}

export function createBioentityLinkSpec(
  tagSchemaField: SchemaField,
  inputFiles: Array<BioentitySummary>
): BioentityLinkSpec {
  return {
    tagSchemaField,
    files: inputFiles.filter(
      (file) =>
        file.tagSchema &&
        tagSchemaField.requiredLink &&
        hasTagSchemaField(tagSchemaField.requiredLink) &&
        tagSchemaField.requiredLink.tagSchema &&
        file.tagSchema.id === tagSchemaField.requiredLink.tagSchema.id
    ),
  };
}

/**
 * Convert bulk antibody to a list of basic folder item specifications (each having name and tags). For the
 * Nth BFI, this has:
 *   - name: <tag schema prefix><N>
 *   - tags: bioentity link fields each mapped to their Nth values
 */
export function getBasicFolderItemSpecs<ForServer extends boolean>(
  bulkAntibody: BulkAntibody,
  {forServer}: {forServer?: ForServer} = {}
): Array<BFISpec<ForServer extends true ? ForServerBFITag : BFITag>> {
  if (!bulkAntibody.bioentityLinkSpecs.length) {
    return [];
  }
  const numFilesList = bulkAntibody.bioentityLinkSpecs.map(
    (bioentityLinkSpec) => bioentityLinkSpec.files.length
  );
  // TODO(T8838): Show the user an error message if `numFilesList` has different #'s of files
  const numBFIs = _.min(numFilesList);
  const bfiSpecs = [];
  for (let bfiIndex = 0; bfiIndex < numBFIs; bfiIndex++) {
    const bfiName = `${bulkAntibody.antibodyNamePrefix}${bfiIndex + 1}`;
    const bfiTags = _.object(
      bulkAntibody.bioentityLinkSpecs.map((bioentityLinkSpec) => {
        const tagName = bioentityLinkSpec.tagSchemaField.name;
        const targetFile = bioentityLinkSpec.files[bfiIndex];
        const tag = forServer
          ? {targetFiles: [pick(targetFile, 'id')]}
          : {targetFiles: [pick(targetFile, 'id', 'name')], value: targetFile.name};
        return [tagName, tag];
      })
    );
    bfiSpecs.push({name: bfiName, tags: bfiTags});
  }
  return bfiSpecs;
}

/**
 * Immutability helpers for assemblies
 */
export const Update = {
  update(bulkAntibody: BulkAntibody, updates: Partial<BulkAntibody>): BulkAntibody {
    return reactUpdate(bulkAntibody, {$merge: updates});
  },
};

function hasTagSchemaField(obj: RequiredLink): obj is RequiredLinkTagSchema | RequiredLinkFolderItemType {
  return (
    (obj as RequiredLinkTagSchema).tagSchema !== undefined ||
    (obj as RequiredLinkFolderItemType).tagSchema !== undefined
  );
}
